import { useEffect, useState, useCallback } from 'react';
import { CreditCard, Clock, Shield } from 'lucide-react';
import { api } from '../../lib/api.js';
import { useAuth } from '../../contexts/auth.js';

interface BillingStatus {
  subscriptionStatus: string;
  trialEndsAt: string | null;
  hasPaymentMethod: boolean;
  trialDaysLeft: number | null;
}

function buildPaymentSetupUrl(orgId: string | null | undefined, returnTo: string): string {
  const authBaseUrl = window.location.hostname === 'localhost' ? '' : 'https://auth.rule42.ai';
  return `${authBaseUrl}/auth/payment-setup?org=${encodeURIComponent(orgId ?? '')}&returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * Trial activation banner — shows in the top-right when user hasn't added payment.
 */
export function TrialBanner() {
  const { claims } = useAuth();
  const [billing, setBilling] = useState<BillingStatus | null>(null);

  useEffect(() => {
    if (!claims?.org_id) return;
    api.get<BillingStatus>(`/api/v1/orgs/me`)
      .then((data) => setBilling(data))
      .catch(() => { /* non-critical */ });
  }, [claims?.org_id]);

  if (!billing || billing.hasPaymentMethod || billing.subscriptionStatus === 'active') return null;

  const daysLeft = billing.trialDaysLeft ?? 0;
  const isExpired = daysLeft <= 0;
  const isUrgent = daysLeft <= 3;
  const paymentUrl = buildPaymentSetupUrl(claims?.org_id, window.location.href);

  return (
    <div className={`fixed top-3 right-3 z-50 flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 shadow-lg backdrop-blur-xl transition-all ${
      isExpired
        ? 'border-red-500/30 bg-red-500/10'
        : isUrgent
          ? 'border-amber-500/30 bg-amber-500/10'
          : 'border-indigo-500/20 bg-indigo-500/8'
    }`}>
      <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${
        isExpired ? 'bg-red-500/20' : isUrgent ? 'bg-amber-500/20' : 'bg-indigo-500/15'
      }`}>
        <Clock className={`h-3.5 w-3.5 ${
          isExpired ? 'text-red-400' : isUrgent ? 'text-amber-400' : 'text-indigo-400'
        }`} />
      </div>
      <div className="text-xs">
        <span className={`font-semibold ${
          isExpired ? 'text-red-300' : isUrgent ? 'text-amber-300' : 'text-white/80'
        }`}>
          {isExpired ? 'Trial expired' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
        </span>
        <span className="text-white/40 ml-1.5">in trial</span>
      </div>
      <a
        href={paymentUrl}
        className={`ml-2 rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${
          isExpired
            ? 'bg-red-500 text-white hover:bg-red-400'
            : 'bg-indigo-500 text-white hover:bg-indigo-400'
        }`}
      >
        Activate
      </a>
    </div>
  );
}

/**
 * Full-screen payment gate — shown on login when no payment method is captured.
 * User can dismiss (skip) but it shows trial days remaining prominently.
 */
export function PaymentGate({ onDismiss }: { onDismiss: () => void }) {
  const { claims } = useAuth();
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const loadBilling = useCallback(async () => {
    try {
      const data = await api.get<BillingStatus>('/api/v1/orgs/me');
      setBilling(data);
    } catch {
      setBilling(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBilling();
  }, [loadBilling]);

  // Don't show gate if payment already captured or not in trial
  if (loading) return null;
  if (!billing || billing.hasPaymentMethod) {
    onDismiss();
    return null;
  }

  const daysLeft = billing.trialDaysLeft ?? 0;
  const isExpired = daysLeft <= 0;
  const orgName = claims?.org_name ?? 'your workspace';
  const paymentUrl = buildPaymentSetupUrl(claims?.org_id, window.location.href);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0a0918]/95 backdrop-blur-sm p-4">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-white/8 bg-[rgba(20,18,45,0.95)] p-8 shadow-2xl backdrop-blur-xl">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/20">
              <CreditCard className="h-7 w-7 text-indigo-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-1">
              {isExpired ? 'Trial Ended' : 'Activate Your Account'}
            </h2>
            <p className="text-sm text-white/50">
              {isExpired
                ? `Your trial for ${orgName} has expired. Add a payment method to continue.`
                : `Add a payment method to ${orgName} to ensure uninterrupted access.`
              }
            </p>
          </div>

          {/* Trial status card */}
          <div className={`rounded-xl border p-4 mb-6 ${
            isExpired
              ? 'border-red-500/20 bg-red-500/5'
              : 'border-indigo-500/15 bg-indigo-500/5'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Clock className={`h-4 w-4 ${isExpired ? 'text-red-400' : 'text-indigo-400'}`} />
                <span className="text-sm font-medium text-white/80">
                  {isExpired ? 'Trial period' : 'Free trial'}
                </span>
              </div>
              <span className={`text-sm font-bold ${isExpired ? 'text-red-400' : 'text-indigo-400'}`}>
                {isExpired ? 'Expired' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`}
              </span>
            </div>
            {!isExpired && (
              <div className="mt-3">
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all"
                    style={{ width: `${Math.max(5, ((14 - daysLeft) / 14) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Benefits */}
          <div className="space-y-2.5 mb-6">
            <div className="flex items-center gap-2.5 text-xs text-white/50">
              <Shield className="h-3.5 w-3.5 text-emerald-400" />
              <span>You won't be charged until your trial ends</span>
            </div>
            <div className="flex items-center gap-2.5 text-xs text-white/50">
              <Shield className="h-3.5 w-3.5 text-emerald-400" />
              <span>Cancel anytime with no obligation</span>
            </div>
            <div className="flex items-center gap-2.5 text-xs text-white/50">
              <Shield className="h-3.5 w-3.5 text-emerald-400" />
              <span>Secured by Stripe — your data stays safe</span>
            </div>
          </div>

          {/* CTA */}
          <a
            href={paymentUrl}
            className="block w-full rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 py-3.5 text-center text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/30 transition-all hover:-translate-y-0.5"
          >
            {isExpired ? 'Add Payment Method' : 'Activate Now'}
          </a>

          {/* Skip option — intentionally subtle */}
          {!isExpired && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={onDismiss}
                className="text-[11px] text-white/20 hover:text-white/35 transition-colors"
              >
                Skip for now ({daysLeft} day{daysLeft !== 1 ? 's' : ''} left in trial)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
